/** Readable lines for the details of an activity log event (docs/adr/ADR-007). */
import {
  ACCESS_LEVEL_LABELS,
  type AccessLevel,
  APP_MODULES,
  type AuditEventResponse,
  type DataScope,
  formatMoney,
  formatPercent,
  formatQuantity,
  type ModuleDefinition,
} from '@k-management/shared';

const MODULES: readonly ModuleDefinition[] = APP_MODULES;

const FIELD_LABELS: Record<string, string> = {
  email: 'E-mail',
  firstName: 'Nome',
  lastName: 'Cognome',
  isAdmin: 'Amministratore',
  isActive: 'Attivo',
  profileId: 'Ruolo',
  name: 'Nome',
  description: 'Descrizione',
  access: 'Accessi',
  code: 'Codice',
  values: 'Colonne',
  active: 'Attiva',
  isDefault: 'Predefinita',
  hidden: 'Nascosta',
  number: 'Versione',
  from: 'Copiata dalla versione',
  validFrom: 'Valida dal',
  closes: 'Chiude la versione',
  closesOn: 'Fino al',
  mode: 'Modifiche',
  isClassification: 'Classificazione',
  columns: 'Colonne',
  clientId: 'Cliente',
  client: 'Cliente',
  referenceName: 'Nome proposta',
  riga: 'Riga',
  typeCode: 'Tipologia',
  subTypeCode: 'Voce',
  quantity: 'Quantità',
  unitCode: 'Unità',
  unitPrice: 'Prezzo unitario',
  discountPercent: 'Sconto %',
  finalAmount: 'Importo finale',
  premiumAmount: 'Premium',
  imponibile: 'Imponibile',
  totale: 'Totale',
  importoSconto: 'Sconto',
  scontoDocumento: 'Sconto sul documento',
  scontiRighe: 'Sconti delle righe',
  costo: 'Costo previsto',
  personalizzato: 'Personalizzato',
  costoPrevisto: 'Costo previsto',
  collegato: 'Collegato',
  scollegato: 'Scollegato da',
  nota: 'Nota',
  copia: 'Copia',
  vatCode: 'IVA',
  referentCode: 'Referente',
  validityDays: 'Validità (giorni)',
  notes: 'Note',
  lines: 'Righe',
  net: 'Imponibile',
  state: 'Stato',
  replaces: 'Sostituisce',
  copiedFrom: 'Copiato da',
};

/** Keys already in the summary of the event. */
const SUMMARY_KEYS = new Set(['email', 'firstName', 'lastName', 'name']);

export function describeAuditDetails(event: AuditEventResponse): string[] {
  const details = event.details;
  if (event.action.endsWith('.created') || event.action.endsWith('.deleted')) {
    return Object.entries(details)
      .filter(([key]) => !SUMMARY_KEYS.has(key))
      .map(([key, value]) => `${FIELD_LABELS[key] ?? key}: ${formatValue(key, value)}`);
  }
  return Object.entries(details).map(([key, value]) => {
    const change = value as { from?: unknown; to?: unknown } | null;
    if (change && typeof change === 'object' && 'from' in change && 'to' in change) {
      return `${FIELD_LABELS[key] ?? key}: ${formatValue(key, change.from)} → ${formatValue(key, change.to)}`;
    }
    return `${FIELD_LABELS[key] ?? key}: ${formatValue(key, value)}`;
  });
}

/** Details that are amounts of money. */
const MONEY_KEYS = new Set([
  'net',
  'unitPrice',
  'finalAmount',
  'total',
  'totale',
  'imponibile',
  'importoSconto',
]);

function formatValue(key: string, value: unknown): string {
  if (typeof value === 'boolean') return value ? 'sì' : 'no';
  if (MONEY_KEYS.has(key) && typeof value === 'string') return formatMoney(value);
  if (key === 'quantity' && typeof value === 'string') return formatQuantity(value);
  if ((key === 'discountPercent' || key === 'sconto') && typeof value === 'string' && value !== '')
    return formatPercent(value);
  if (key === 'access' && value && typeof value === 'object') return formatAccess(value);
  if (key === 'profileId' && value === null) return 'nessuno';
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value))
    return value.length > 0 ? value.map((item) => formatValue('', item)).join(', ') : '—';
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([field, item]) => `${FIELD_LABELS[field] ?? field}: ${formatValue(field, item)}`)
      .join('; ');
  }
  return String(value);
}

function formatAccess(value: object): string {
  const access = value as {
    modules?: Record<string, { level: AccessLevel; scope?: DataScope } | AccessLevel>;
    featureOverrides?: Record<string, boolean>;
  };
  const parts = MODULES.filter((module) => access.modules?.[module.key]).map((module) => {
    // Events written before ADR-008 hold the bare level.
    const entry = access.modules![module.key]!;
    const { level, scope } = typeof entry === 'string' ? { level: entry, scope: undefined } : entry;
    const scopeText = scope && module.dataScope ? `, ${module.dataScope[scope].toLowerCase()}` : '';
    return `${module.label} ${ACCESS_LEVEL_LABELS[level].toLowerCase()}${scopeText}`;
  });
  const features = MODULES.flatMap((module) => module.features);
  const hidden = features.filter((feature) => access.featureOverrides?.[feature.key] === false);
  const granted = features.filter((feature) => access.featureOverrides?.[feature.key] === true);
  if (hidden.length > 0) parts.push(`senza ${hidden.map((feature) => feature.label).join(', ')}`);
  if (granted.length > 0) parts.push(`con ${granted.map((feature) => feature.label).join(', ')}`);
  return parts.length > 0 ? parts.join('; ') : 'nessun modulo';
}
